import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = ["Capital Raising", "M&A Advisory", "Impact Investing", "Family Offices"];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone">{label}</span>
    <input
      {...props}
      className="mt-2 w-full bg-transparent border-b border-hairline py-3 text-parchment placeholder:text-stone/40 focus:border-gold focus:outline-none transition-colors duration-300"
    />
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Your inquiry has been received. We will be in touch.");
      setForm({ name: "", email: "", company: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="relative py-28 lg:py-40 border-t border-hairline">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-gold">Get Started</span>
          <h2 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-parchment mt-6 text-balance">
            Let's build something<br />that <span className="italic text-gold">matters.</span>
          </h2>
          <p className="text-stone leading-relaxed mt-6 max-w-md">
            Whether you're preparing to raise capital, pursuing strategic growth, exploring acquisitions or seeking exceptional private investment opportunities — EQ Capital Group is ready to become your trusted strategic partner.
          </p>
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold mt-6">
            Strategic Capital · Trusted Relationships · Lasting Impact
          </p>

          <div className="mt-12 space-y-6 border-t border-hairline pt-10">
            <div>
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone">Main Office</span>
              <p className="font-serif text-2xl text-parchment mt-1">+1 609-851-2160</p>
            </div>
            <div>
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone">Email</span>
              <p className="mt-1"><a href="mailto:Erez@Eqcapitalgroup.com" className="font-serif text-2xl text-parchment link-underline hover:text-gold transition-colors duration-300">Erez@Eqcapitalgroup.com</a></p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          {sent ? (
            <div data-testid="contact-success" className="h-full min-h-[400px] flex flex-col items-start justify-center border border-hairline bg-surface/30 p-12">
              <span className="h-14 w-14 rounded-full border border-gold flex items-center justify-center text-gold font-serif text-2xl">✓</span>
              <h3 className="font-serif font-light text-4xl text-parchment mt-8">Thank you.</h3>
              <p className="text-stone mt-3 max-w-md leading-relaxed">Your inquiry has reached our desk. A member of the EQ Capital Group team will respond personally and in confidence.</p>
              <button data-testid="contact-reset" onClick={() => setSent(false)} className="mt-8 font-mono text-xs tracking-[0.2em] uppercase text-gold link-underline">Send another</button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <Field data-testid="input-name" label="Full Name *" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                <Field data-testid="input-email" label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" />
              </div>
              <Field data-testid="input-company" label="Company" value={form.company} onChange={set("company")} placeholder="Company / Fund" />

              <div>
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone">Area of Interest</span>
                <div className="mt-3 flex flex-wrap gap-3">
                  {INTERESTS.map((it) => (
                    <button
                      type="button"
                      key={it}
                      data-testid={`interest-${it}`}
                      onClick={() => setForm((f) => ({ ...f, interest: it }))}
                      className={`font-mono text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 border transition-colors duration-300 ${
                        form.interest === it ? "border-gold bg-gold text-ink" : "border-hairline text-stone hover:border-gold hover:text-gold"
                      }`}
                    >
                      {it}
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone">Message *</span>
                <textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="Tell us about your mandate..."
                  className="mt-2 w-full bg-transparent border-b border-hairline py-3 text-parchment placeholder:text-stone/40 focus:border-gold focus:outline-none transition-colors duration-300 resize-none"
                />
              </label>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="group inline-flex items-center gap-4 font-mono text-xs tracking-[0.2em] uppercase border border-gold text-gold px-8 py-4 hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <>Submit Inquiry <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" /></>}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
